// Fiók-detail SZEMÉLY-szekció — a fiók kötései EGY minőségre (`kind`): Kapcsolattartók VAGY
// Ügyféladminisztrátorok. Egy komponens, két példány (a két szekció csak a `kind`-ban, a címben és az
// ikonban tér el — a viselkedés bájtra ugyanaz, tehát nem másoljuk kétszer).
//
// MIÉRT KÉT SZEKCIÓ (és nem egy „Kapcsolatok"): a régi CRM-ben is két külön tab volt (`contacts` ·
// `client_admins`), mert a két halmaz MÁS: az ügyféladminisztrátor BELSŐ kolléga (mi felelünk a fiókért), a
// kapcsolattartó a PARTNER oldalán áll. Egy közös listában a kettő összemosódna — és a „hozzáadás" gomb sem
// tudná, melyik identity-poolból válasszon (a sheet is más típusra szűr: internal vs partner).
//
// A SOR HÁROM+EGY SLOTJA (`RelatedList`): személy (avatar + locale-tudatos név) · menedzser-típus (csak
// adminnál) · állapot-badge-ek (elsődleges / portál-hozzáférés) · sor-akciók (szerkesztés · eltávolítás).
// A sor-akciók MIATT a sor nem kattintható — a kötésnek nincs saját oldala, tehát az akciók ITT kellenek.
import { type AccountDetailSchema, type PortalRole } from '@atlas/contracts';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';
import { useState } from 'react';
import { type z } from 'zod';

import {
  AccountAffiliationFormSheet,
  type AffiliationEditTarget,
} from '@/components/accounts/account-affiliation-form-sheet';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DomainBadge } from '@/components/shared/domain-badge';
import { FormSection } from '@/components/shared/form-section';
import { PersonChip } from '@/components/shared/person-chip';
import { RelatedList, type RelatedListItem } from '@/components/shared/related-list';
import { type SectionIcon } from '@/components/shared/section-placeholder';
import { portalAccessKeys } from '@/features/accounts/portal-access-keys';
import { Button } from '@/components/ui/button';
import { accountKeys } from '@/features/accounts/api';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

type AccountDetail = z.infer<typeof AccountDetailSchema>;
type AccountPerson = AccountDetail['admins'][number];
type AffiliationDetail = AccountDetail['affiliationDetails'][number];

export function AccountPeopleSection({
  accountId,
  kind,
  sectionId,
  title,
  emptyIcon,
  people,
  affiliationDetails,
  canAssign,
  canRemove,
  t,
  onChanged,
}: {
  accountId: string;
  /** A kötés minősége — ez dönti el a sheet identity-pooljét (admin → belsős, contact → partner) is. */
  kind: 'admin' | 'contact';
  sectionId: string;
  title: string;
  emptyIcon: SectionIcon;
  people: readonly AccountPerson[];
  /** A fiók KÖTÉSEI (flag-ekkel) — a sor-badge-ek és a szerkesztő-előtöltés forrása. */
  affiliationDetails: readonly AffiliationDetail[];
  /**
   * KÉT KÜLÖN JOG, mert a szerver is kettőt néz: a kötés LÉTREHOZÁSA/módosítása `admins.assign` ·
   * `contacts.assign`, az ELTÁVOLÍTÁSA `admins.remove` · `contacts.remove` — a `kind` szerint. Egy közös
   * „canManage" hamis affordanciát adna: megjelenne a kuka annak is, aki csak hozzáadni tud.
   */
  canAssign: boolean;
  canRemove: boolean;
  t: TFunction;
  /** Eltávolítás után a hívó (a route) újratölti a detailt — a lista a detailből jön. */
  onChanged?: () => void;
}): JSX.Element {
  // Add (target=null) / edit (target=kötés) egy sheet-példányon; a törlés-cél külön (megerősítő modal).
  const { can } = usePermissions();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AffiliationEditTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AffiliationEditTarget | null>(null);

  const removeMutation = useEntityMutation<AffiliationEditTarget, unknown>({
    mutationFn: (target) =>
      api.accountAffiliationRemove.mutation({
        params: { id: accountId, kind: target.kind, identityId: target.identityId },
      }),
    invalidateKeys: [accountKeys.detail(accountId), accountKeys.lists()],
    messages: {
      success: t('toast:accounts.affiliationRemoved'),
      conflict: t('toast:accounts.conflict'),
      forbidden: t('toast:accounts.forbidden'),
      validation: t('toast:accounts.validation'),
      error: t('toast:accounts.error'),
    },
  });

  const flagsOf = (identityId: string): AffiliationDetail | undefined =>
    affiliationDetails.find((a) => a.identityId === identityId && a.kind === kind);

  /**
   * A PARTNER-PORTÁL SZEREPEK a fiókon — a sor-badge forrása.
   *
   * MIÉRT KÜLÖN LEKÉRÉS: a kötés-sor csak egy `portalAccess` BITET hordoz („van-e hozzáférése"), a tényleges
   * szerep viszont Odin-GRANT, és azt a `portal-access` végpont adja. A badge a szerepet mondja ki
   * (user-döntés), ezért a bit nem elég.
   *
   * CSAK a kapcsolattartó-szekcióban kérjük le: az ÜGYFÉLADMIN kötésnek nincs portál-szerepe — élesben 867
   * ilyen kötés van, és EGYNEK SINCS (mérve). Admin-szekcióban a lekérés tiszta pazarlás lenne.
   */
  const portalEnabled = kind === 'contact' && can('portal_access.view');
  const portalQuery = api.portalAccessList.useQuery(
    portalAccessKeys.list(accountId),
    { params: { id: accountId } },
    { queryKey: portalAccessKeys.list(accountId), enabled: portalEnabled },
  );
  const portalRoleOf = (identityId: string): PortalRole | null => {
    if (!portalEnabled || portalQuery.data?.status !== 200) return null;
    return portalQuery.data.body.rows.find((r) => r.identityId === identityId)?.role ?? null;
  };

  const items: RelatedListItem[] = people.map((p) => {
    const flags = flagsOf(p.id);
    const portalRole = portalRoleOf(p.id);
    const managerLabel =
      kind === 'admin' && flags?.managerType
        ? t(`accounts.affiliation.managerLabel.${flags.managerType}`, {
            defaultValue: flags.managerType,
          })
        : undefined;
    // Feloldatlan (törölt) személy → a picker-előtöltés címkéje a közös „Törölt felhasználó".
    const target: AffiliationEditTarget = {
      identityId: p.id,
      kind,
      name: p.name ?? t('common.deletedUser'),
    };
    return {
      id: p.id,
      title: (
        <PersonChip
          givenName={p.givenName}
          familyName={p.familyName}
          name={p.name}
          avatarUrl={p.avatarUrl}
          accentColor={p.teamColor}
          size="sm"
        />
      ),
      meta: managerLabel,
      // Badge-ek KISBETŰVEL (design.md) — állapot-jelzés, nem cím.
      status:
        flags?.isPrimary || portalRole !== null ? (
          <span className="flex items-center gap-1.5">
            {flags?.isPrimary === true && (
              <DomainBadge kind="marker" value="primary" label={t('accounts.primaryBadge')} />
            )}
            {portalRole !== null && (
              <DomainBadge
                kind="marker"
                value={portalRole}
                label={t(`portalAccess.roles.${portalRole}`)}
              />
            )}
          </span>
        ) : undefined,
      // Sor-akciók JOGONKÉNT: a ceruza a kötés-MÓDOSÍTÁS (assign), a kuka az ELTÁVOLÍTÁS (remove) joga —
      // a kettő külön adható. Egyik sem → a sor tiszta olvasás (a BFF is 403-at adna).
      actions:
        canAssign || canRemove ? (
          <span className="flex items-center gap-0.5">
            {canAssign && (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded p-1 focus-visible:outline-none focus-visible:ring-2"
                onClick={() => {
                  setEditTarget(target);
                  setSheetOpen(true);
                }}
                aria-label={t('accounts.affiliation.editAria', { name: p.name })}
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive focus-visible:ring-ring rounded p-1 focus-visible:outline-none focus-visible:ring-2"
                onClick={() => setDeleteTarget(target)}
                aria-label={t('accounts.affiliation.removeAria', { name: p.name })}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </span>
        ) : undefined,
    };
  });

  return (
    <FormSection id={sectionId} title={title}>
      <RelatedList
        items={items}
        showAllLabel={(total) => t('accounts.relatedTotal', { count: total })}
        emptyLabel={kind === 'admin' ? t('accounts.adminsEmpty') : t('accounts.contactsEmpty')}
        emptyIcon={emptyIcon}
        errorLabel={t('accounts.relatedError')}
        forbiddenLabel={t('accounts.relatedForbidden')}
        retryLabel={t('accounts.relatedRetry')}
      />

      {/* A „hozzáadás" belépő a szekció TARTALMÁNAK VÉGÉN, BALRA (design.md: a folyamatot indító, önálló gomb
          a tartalomban balra igazodik — a sáv-/lábléc-akciók mennek jobbra). */}
      {canAssign && (
        <div className="flex">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setEditTarget(null);
              setSheetOpen(true);
            }}
          >
            <PlusIcon />
            {kind === 'admin' ? t('accounts.addAdmin') : t('accounts.addContact')}
          </Button>
        </div>
      )}

      {/* A sheet csak írás-joggal létezik (a nyitó gombok is jog-kapuzva). A `presetKind` miatt a felvett kötés
          MINDIG ebbe a szekcióba kerül — nem lehet véletlenül a másikba menteni. */}
      {canAssign && (
        <AccountAffiliationFormSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          accountId={accountId}
          target={editTarget}
          affiliationDetails={[...affiliationDetails]}
          presetKind={kind}
        />
      )}

      {/* Kötés eltávolítása — a kötést bontja, az identity GLOBÁLIS profilja érintetlen (ezt a szöveg is kimondja). */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('accounts.affiliation.removeTitle')}
        description={t('accounts.affiliation.removeDesc', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('accounts.affiliation.remove')}
        cancelLabel={t('accounts.cancel')}
        pendingLabel={t('accounts.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          removeMutation.mutate(deleteTarget, {
            onSettled: () => {
              setDeleteTarget(null);
              onChanged?.();
            },
          });
        }}
      />
    </FormSection>
  );
}
